<template>
  <v-container fluid>
    <v-card :loading="loading">
      <v-card-title>
        <h2>
          <v-btn depressed class="mx-2" icon text :to="{ name: 'tipo-documento-list' }">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span v-if="loading">Carregando...</span>
          <span
            v-else
          >{{tipo.id === "" ? "Novo tipo de documento" : `Alterar tipo de documento: ${tipo.descricao}`}}</span>
        </h2>
      </v-card-title>
      <form @submit.prevent="submitForm">
        <v-card-text>
          <v-text-field
            v-model="tipo.descricao"
            :error-messages="descricaoErrors"
            :counter="50"
            label="Descrição"
            v-touch="tipo.descricao"
          ></v-text-field>
          <v-select
            v-model="tipo.tipos_permitidos"
            v-touch="tipo.tipos_permitidos"
            :error-messages="permitidosErrors"
            :items="tiposDisponiveis"
            label="Tipos de arquivos permitidos"
            multiple
          />
          <v-text-field
            v-model.number="tipo.tamanho_maximo"
            :error-messages="tamanhoErrors"
            label="Tamanho máximo permitido"
            v-touch="tipo.tamanho_maximo"
            suffix="MB"
          ></v-text-field>
          <v-select
            v-model="tipo.ids_tipo_proprietario"
            v-touch="tipo.ids_tipo_proprietario"
            :error-messages="proprietariosErrors"
            :items="proprietariosDisponiveis"
            item-text="descricao"
            item-value="id"
            label="Tipos de proprietário"
            multiple
          />
          <h3 class="headline font-weight-thin mt-2">Alertas de vencimento</h3>
          <v-text-field
            v-model.number="tipo.dias_alerta_vencimento1"
            v-touch="tipo.dias_alerta_vencimento1"
            :error-messages="dias1Errors"
            label="Primeiro alerta"
            suffix="dias"
            hint="Deixe em branco ou zero para não emitir alertas"
            persistent-hint
          />
          <v-text-field
            v-model.number="tipo.dias_alerta_vencimento2"
            v-touch="tipo.dias_alerta_vencimento2"
            :error-messages="dias2Errors"
            label="Segundo alerta"
            suffix="dias"
            :disabled="disabledDias2"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn depressed color="primary" @click="submit" :loading="loading">Salvar</v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import { validationMixin } from "vuelidate";
import { required, maxLength, numeric } from "vuelidate/lib/validators";
import lessThan from "../validators/lessThan";

export default {
  mixins: [validationMixin],
  validations() {
    const regras = {
      tipo: {
        descricao: { required, maxLength: maxLength(50) },
        tamanho_maximo: { required, numeric },
        ids_tipo_proprietario: { required },
        tipos_permitidos: { required },
        dias_alerta_vencimento1: { numeric },
        dias_alerta_vencimento2: {
          numeric
        }
      }
    };
    if (!this.disabledDias2) {
      regras.tipo.dias_alerta_vencimento2.menor = lessThan(
        "dias_alerta_vencimento1"
      );
    }
    return regras;
  },
  data() {
    return {
      loading: false,
      tipo: {
        id: "",
        descricao: "",
        ids_tipo_proprietario: [],
        tamanho_maximo: "",
        tipos_permitidos: []
      },
      tiposDisponiveis: [
        {
          value: "imagem",
          text: "Imagem"
        },
        {
          value: "audio",
          text: "Áudio"
        },
        {
          value: "pdf",
          text: "PDF"
        },
        {
          value: "word",
          text: "Word"
        },
        {
          value: "excel",
          text: "Excel"
        }
      ]
    };
  },

  computed: {
    descricaoErrors() {
      const errors = [];
      if (!this.$v.tipo.descricao.$dirty) return errors;
      !this.$v.tipo.descricao.maxLength &&
        errors.push("Somente 50 caracteres.");
      !this.$v.tipo.descricao.required &&
        errors.push("Descrição do tipo de documeto é obrigatório");
      return errors;
    },
    tamanhoErrors() {
      const errors = [];
      if (!this.$v.tipo.tamanho_maximo.$dirty) return errors;
      !this.$v.tipo.tamanho_maximo.numeric && errors.push("Somente números");
      !this.$v.tipo.tamanho_maximo.required &&
        errors.push("Informe o tamanho máximo permitido dos arquivos");
      return errors;
    },
    proprietariosErrors() {
      const errors = [];
      if (!this.$v.tipo.ids_tipo_proprietario.$dirty) return errors;
      !this.$v.tipo.ids_tipo_proprietario.required &&
        errors.push("Informe ao menos um tipo de proprietário");
      return errors;
    },
    permitidosErrors() {
      const errors = [];
      if (!this.$v.tipo.tipos_permitidos.$dirty) return errors;
      !this.$v.tipo.tipos_permitidos.required &&
        errors.push("Informe ao menos um tipo permitido de arquivo");
      return errors;
    },
    dias1Errors() {
      const errors = [];
      if (!this.$v.tipo.dias_alerta_vencimento1.$dirty) return errors;
      !this.$v.tipo.dias_alerta_vencimento1.numeric &&
        errors.push("Somente números");
      return errors;
    },
    dias2Errors() {
      const errors = [];
      if (!this.$v.tipo.dias_alerta_vencimento2.$dirty) return errors;
      !this.$v.tipo.dias_alerta_vencimento2.numeric &&
        errors.push("Somente números");
      !this.disabledDias2 &&
        !this.$v.tipo.dias_alerta_vencimento2.menor &&
        errors.push("O prazo do segundo alerta deve ser menor que o primeiro");
      return errors;
    },
    disabledDias2() {
      return (
        isNaN(parseInt(this.tipo.dias_alerta_vencimento1)) ||
        this.tipo.dias_alerta_vencimento1 == 0
      );
    },
    ...mapState({
      proprietariosDisponiveis: state => state.documento.tiposProprietario
    })
  },

  methods: {
    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.loading = true;
        try {
          this.tipo.dias_alerta_vencimento1 = isNaN(
            parseInt(this.tipo.dias_alerta_vencimento1)
          )
            ? 0
            : this.tipo.dias_alerta_vencimento1;

          this.tipo.dias_alerta_vencimento2 = isNaN(
            parseInt(this.tipo.dias_alerta_vencimento2)
          )
            ? 0
            : this.tipo.dias_alerta_vencimento2;

          await this.$store.dispatch("documento/gravarTipo", this.tipo);
          this.$router.replace({ name: "tipo-documento-list" });
        } catch (err) {
          console.log(err);
          this.loading = false;
          this.$store.commit("ui/setSnackbar", {
            text: "Não foi possível salvar o tipo de documento."
          });
        }
      }
    }
  },
  async mounted() {
    this.loading = true;
    const p = [this.$store.dispatch("documento/listarTiposProprietario")];
    if (this.$route.params.id) {
      p.push(
        this.$store.dispatch("documento/carregarTipo", this.$route.params.id)
      );
    }
    try {
      const r = await Promise.all(p);
      if (r[1]) {
        this.tipo = r[1];
      }
    } catch (err) {
      console.log(err);
    } finally {
      this.loading = false;
    }
  },
  watch: {
    disabledDias2(dis) {
      if (dis) {
        this.tipo.dias_alerta_vencimento2 = 0;
      }
    }
  }
};
</script>
